import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import styles from "./privacy.module.css";

// Public, unauthenticated privacy policy for the Dropskip platform and the
// Dropskip Shopify public app. This is the canonical page linked from the
// Shopify App Store listing and from the embedded app's "Data privacy" and
// "Support" screens. Kept factually aligned with what the app actually does:
// read-only Shopify access, no write scopes, data purged on uninstall.

const LAST_UPDATED = "September 3, 2026";
const CONTACT_EMAIL = "support@redefinedlogistics.com";
const OPERATOR = "Redefined Logistics";

const SECTIONS = [
  { id: "who-we-are", title: "Who we are" },
  { id: "scope", title: "Scope of this policy" },
  { id: "what-we-collect", title: "Information we collect" },
  { id: "protected-customer-data", title: "Shopify Protected Customer Data" },
  { id: "how-we-use", title: "How we use information" },
  { id: "legal-bases", title: "Legal bases (GDPR)" },
  { id: "sharing", title: "How we share information" },
  { id: "subprocessors", title: "Sub-processors" },
  { id: "transfers", title: "International data transfers" },
  { id: "retention", title: "Data retention" },
  { id: "deletion", title: "Data deletion & uninstalling" },
  { id: "security", title: "Security" },
  { id: "your-rights", title: "Your rights & choices" },
  { id: "shopify-webhooks", title: "Shopify compliance webhooks" },
  { id: "cookies", title: "Cookies & tracking" },
  { id: "children", title: "Children's data" },
  { id: "changes", title: "Changes to this policy" },
  { id: "contact", title: "Contact us" },
];

export default function PrivacyPolicy() {
  useEffect(() => {
    document.title = "Privacy Policy — Dropskip";
  }, []);

  return (
    <div className={styles.page}>
      <div className={styles.topbar}>
        <Link to="/" className={styles.brand}>
          <img src="/DropSkipLogo.png" alt="Dropskip" />
          <span>Dropskip</span>
        </Link>
        <Link to="/login" className={styles.backLink}>
          Back to app
        </Link>
      </div>

      <main className={styles.container}>
        <header className={styles.header}>
          <span className={styles.eyebrow}>Legal</span>
          <h1>Privacy Policy</h1>
          <p className={styles.meta}>Last updated: {LAST_UPDATED}</p>
        </header>

        <p className={styles.lede}>
          This Privacy Policy explains how Dropskip&trade;, operated by{" "}
          {OPERATOR}, collects, uses, discloses, retains, and protects
          information when a Shopify merchant installs and uses the Dropskip app
          and the Dropskip platform for demand forecasting, reorder planning,
          and lost-sales analysis. Dropskip requests <strong>read-only</strong>{" "}
          access to Shopify data and never requests permission to create, edit,
          or delete anything in your store.
        </p>

        <nav className={styles.toc} aria-label="Table of contents">
          <h2>Contents</h2>
          <ol>
            {SECTIONS.map(({ id, title }) => (
              <li key={id}>
                <a href={`#${id}`}>{title}</a>
              </li>
            ))}
          </ol>
        </nav>

        <section id="who-we-are" className={styles.section}>
          <h2>1. Who we are</h2>
          <p>
            Dropskip is a Shopify application and web platform operated by{" "}
            {OPERATOR}. For data that Dropskip receives from your Shopify store
            &mdash; including any personal data relating to your customers
            &mdash; you (the merchant) are the <strong>data controller</strong>{" "}
            and Dropskip acts as a{" "}
            <strong>data processor / service provider</strong> processing that
            data on your behalf and on your instructions. For the account and
            billing information you provide to us directly, Dropskip is the
            controller.
          </p>
          <p>
            If you have any question about this policy or how your data is
            handled, contact us at{" "}
            <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>.
          </p>
        </section>

        <section id="scope" className={styles.section}>
          <h2>2. Scope of this policy</h2>
          <p>
            This policy covers the Dropskip embedded app inside the Shopify
            admin, the Dropskip web platform, and the backend services that
            sync and store your store&rsquo;s data. It does not cover Shopify
            itself (see Shopify&rsquo;s own privacy policy) or any third-party
            website or service you may link to Dropskip separately.
          </p>
        </section>

        <section id="what-we-collect" className={styles.section}>
          <h2>3. Information we collect</h2>

          <h3>3.1 Store data synced from Shopify</h3>
          <p>
            When you connect your store, Dropskip uses the OAuth scopes you
            grant to read the following data through Shopify&rsquo;s Admin API.
            All access is read-only.
          </p>
          <dl className={styles.dataList}>
            <div className={styles.dataRow}>
              <dt>Products &amp; variants</dt>
              <dd>
                Title, SKU, price, images, options and variant details. Used to
                build per-SKU demand forecasts and product-level views.
              </dd>
            </div>
            <div className={styles.dataRow}>
              <dt>Orders &amp; order history</dt>
              <dd>
                Line items, quantities, totals, discounts, currency,
                fulfillment status and order timestamps &mdash; including up to
                approximately 36 months of historical orders on first connect.
                Used for forecasting and lost-sales analysis only.
              </dd>
            </div>
            <div className={styles.dataRow}>
              <dt>Customer details on orders</dt>
              <dd>
                Name, shipping address, email address and phone number attached
                to synced orders. This is Shopify Protected Customer Data
                &mdash; see section 4.
              </dd>
            </div>
            <div className={styles.dataRow}>
              <dt>Inventory levels</dt>
              <dd>
                Per-location on-hand quantities for locations you have mapped to
                a warehouse. Keeps stock positions current for forecasting and
                reorder timing.
              </dd>
            </div>
            <div className={styles.dataRow}>
              <dt>Locations</dt>
              <dd>
                Your Shopify fulfillment locations, mapped to warehouses in
                Dropskip so inventory is attributed correctly.
              </dd>
            </div>
            <div className={styles.dataRow}>
              <dt>Fulfillments &amp; returns</dt>
              <dd>
                Fulfillment and return records, used to reconcile what actually
                shipped and what came back.
              </dd>
            </div>
            <div className={styles.dataRow}>
              <dt>Payment disputes, store credit (requested, not yet active)</dt>
              <dd>
                These scopes are requested for planned returns- and
                dispute-aware forecasting. Nothing from these scopes is synced
                into or stored in your workspace today.
              </dd>
            </div>
            <div className={styles.dataRow}>
              <dt>Shopify access token</dt>
              <dd>
                A long-lived, read-only OAuth access token issued for your shop,
                stored so Dropskip can refresh the data above in the background.
                It is never used to write to your store.
              </dd>
            </div>
          </dl>

          <h3>3.2 Account &amp; workspace information</h3>
          <p>
            When you create or link a Dropskip workspace we collect the
            information needed to set up your account: your name, work email
            address, organization / company name, the Shopify shop domain, and
            (for teammates you invite) their names and email addresses. Billing
            is handled through Shopify or our billing provider; we receive plan,
            subscription status and usage data, not full card numbers.
          </p>

          <h3>3.3 Information collected automatically</h3>
          <p>
            Like most web applications, our servers automatically log technical
            information such as IP address, browser and device type, pages
            requested, timestamps, and error diagnostics. We use this for
            security, debugging, and to keep the service reliable.
          </p>
        </section>

        <section id="protected-customer-data" className={styles.section}>
          <h2>4. Shopify Protected Customer Data</h2>
          <p>
            Some order data Dropskip receives is classified by Shopify as{" "}
            <strong>Protected Customer Data</strong>, including customer name,
            email, phone, and shipping address. We handle it under
            Shopify&rsquo;s Protected Customer Data requirements:
          </p>
          <ul>
            <li>
              We collect it only where it is required for the app&rsquo;s
              function &mdash; producing accurate shipping, fulfillment and
              lost-sales records tied to an order.
            </li>
            <li>
              We <strong>do not</strong> use customer personal data for
              marketing, advertising, profiling of individuals, or any purpose
              unrelated to the forecasting and planning features you installed
              the app for.
            </li>
            <li>
              We <strong>do not</strong> sell customer personal data or share it
              with third parties for their own purposes.
            </li>
            <li>
              Access is restricted to the minimum staff and systems needed to
              operate the service, encrypted in transit and at rest, and
              retained only as long as described in section 10.
            </li>
            <li>
              You can ask us to stop processing, or to delete, customer data at
              any time (see sections 11 and 14).
            </li>
          </ul>
        </section>

        <section id="how-we-use" className={styles.section}>
          <h2>5. How we use information</h2>
          <p>We use the information described above to:</p>
          <ul>
            <li>
              Generate demand forecasts, reorder recommendations, stockout-risk
              scores and lost-sales analysis for your store.
            </li>
            <li>
              Display dashboards, reports and product-level views in Dropskip.
            </li>
            <li>
              Answer questions you ask Dropskip&rsquo;s in-app assistant about
              your own inventory and sales.
            </li>
            <li>Set up, authenticate, secure and support your account.</li>
            <li>
              Keep the sync healthy &mdash; refreshing tokens, retrying failed
              pulls, and notifying you if the connection needs attention.
            </li>
            <li>
              Maintain security, prevent abuse, debug problems, and meet legal
              obligations.
            </li>
            <li>
              Produce aggregated, de-identified statistics that do not identify
              you, your store, or any individual, to improve our forecasting
              models and the product.
            </li>
          </ul>
          <div className={styles.callout}>
            <p>
              We never use your data, or your customers&rsquo; data, to train
              models that are shared across unrelated merchants in a way that
              could expose your individual store or customer records, and we
              never sell your data.
            </p>
          </div>
        </section>

        <section id="legal-bases" className={styles.section}>
          <h2>6. Legal bases (GDPR / UK GDPR)</h2>
          <p>
            Where the GDPR or UK GDPR applies, we rely on the following legal
            bases:
          </p>
          <ul>
            <li>
              <strong>Contract</strong> &mdash; to provide the app and platform
              you signed up for.
            </li>
            <li>
              <strong>Legitimate interests</strong> &mdash; to secure, debug,
              and improve the service, and to produce aggregated analytics,
              balanced against your rights.
            </li>
            <li>
              <strong>Legal obligation</strong> &mdash; to comply with tax,
              accounting and lawful requests.
            </li>
            <li>
              <strong>Your instructions as controller</strong> &mdash; for the
              customer personal data we process on your behalf, our processing
              is governed by your instructions and our data processing terms.
            </li>
          </ul>
        </section>

        <section id="sharing" className={styles.section}>
          <h2>7. How we share information</h2>
          <p>We share information only in these limited circumstances:</p>
          <ul>
            <li>
              <strong>Within our own systems.</strong> The embedded app passes
              data to Dropskip&rsquo;s backend, which stores and processes it to
              power the features described above. Shopify Admin API calls are
              made by our provider service on your behalf.
            </li>
            <li>
              <strong>Service providers / sub-processors.</strong>{" "}
              Infrastructure, hosting, database, logging and error-monitoring
              vendors that process data under contract, only on our
              instructions (see section 8).
            </li>
            <li>
              <strong>Legal and safety.</strong> Where required by law, valid
              legal process, or to protect the rights, property or safety of
              Dropskip, our users, or the public.
            </li>
            <li>
              <strong>Business transfer.</strong> If Dropskip or {OPERATOR} is
              involved in a merger, acquisition, or sale of assets, data may be
              transferred as part of that transaction, subject to this policy.
            </li>
          </ul>
          <p>
            We do <strong>not</strong> sell personal data, and we do not share
            it with advertisers or data brokers.
          </p>
        </section>

        <section id="subprocessors" className={styles.section}>
          <h2>8. Sub-processors</h2>
          <p>
            We use a small number of infrastructure providers to run the service
            &mdash; cloud hosting and compute, managed databases, and logging /
            error-monitoring. Each is bound by a data processing agreement and
            may only process data to provide services to us. A current list of
            sub-processors is available on request by emailing{" "}
            <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>. We will give
            notice of material changes so you can object.
          </p>
        </section>

        <section id="transfers" className={styles.section}>
          <h2>9. International data transfers</h2>
          <p>
            Dropskip and its providers may process data in countries other than
            the one where you or your customers are located. Where we transfer
            personal data internationally, we use appropriate safeguards such as
            the European Commission&rsquo;s Standard Contractual Clauses (and the
            UK Addendum where relevant), or rely on an adequacy decision.
          </p>
        </section>

        <section id="retention" className={styles.section}>
          <h2>10. Data retention</h2>
          <ul>
            <li>
              <strong>Synced store data</strong> (products, orders, inventory,
              locations, customer details on orders) is retained for as long as
              your store is connected, so historical context stays available for
              forecasting.
            </li>
            <li>
              <strong>On uninstall</strong>, syncing stops immediately and the
              stored Shopify access token is revoked. Your synced store data and
              store configuration are deleted from our systems as part of the
              uninstall clean-up, and in any case within 30 days, except where
              we must retain limited records to comply with law or resolve
              disputes.
            </li>
            <li>
              <strong>Shopify&rsquo;s shop-redaction request</strong>, which
              Shopify sends 48 hours after uninstall, is used to confirm the
              purge has completed.
            </li>
            <li>
              <strong>Account and billing records</strong> are retained for the
              life of the account plus the period required for tax and
              accounting.
            </li>
            <li>
              <strong>Technical logs</strong> are retained for a short period
              (typically up to 90 days) then deleted or de-identified.
            </li>
          </ul>
        </section>

        <section id="deletion" className={styles.section}>
          <h2>11. Data deletion &amp; uninstalling</h2>
          <p>
            You can disconnect Dropskip at any time by uninstalling it from your
            Shopify admin (Settings &rarr; Apps and sales channels).
            Uninstalling immediately revokes Dropskip&rsquo;s access token and
            stops all syncing. To request deletion of any remaining data, or
            deletion of a specific customer&rsquo;s data, email{" "}
            <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a> and we will
            action it, typically within 30 days.
          </p>
        </section>

        <section id="security" className={styles.section}>
          <h2>12. Security</h2>
          <p>
            We protect data with measures appropriate to its sensitivity,
            including encryption in transit (TLS) and at rest, scoped and
            least-privilege access controls, HMAC verification of every Shopify
            webhook, short-lived internal service credentials, network isolation
            of backend services, and monitoring and logging. No method of
            transmission or storage is completely secure; if we become aware of
            a breach affecting your data we will notify you and the relevant
            authorities as required by law.
          </p>
        </section>

        <section id="your-rights" className={styles.section}>
          <h2>13. Your rights &amp; choices</h2>
          <p>
            Depending on where you or your customers are located, applicable law
            (including the GDPR, UK GDPR, and US state privacy laws such as the
            CCPA/CPRA) may provide rights to:
          </p>
          <ul>
            <li>Access the personal data we hold and receive a copy.</li>
            <li>Correct inaccurate data.</li>
            <li>Delete data, subject to legal retention exceptions.</li>
            <li>Restrict or object to certain processing.</li>
            <li>Data portability.</li>
            <li>
              Not receive discriminatory treatment for exercising these rights.
            </li>
          </ul>
          <p>
            Because Dropskip processes customer personal data on behalf of the
            merchant, a customer wishing to exercise rights over their data
            should contact the merchant (the store they purchased from). We will
            assist the merchant in responding. Merchants and individuals can
            reach us directly at{" "}
            <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>. You also
            have the right to complain to your local data protection authority.
          </p>
        </section>

        <section id="shopify-webhooks" className={styles.section}>
          <h2>14. Shopify compliance webhooks</h2>
          <p>
            Dropskip implements the mandatory Shopify privacy webhooks. Each is
            verified by HMAC before processing:
          </p>
          <ul>
            <li>
              <strong>customers/data_request</strong> &mdash; when a merchant
              forwards a customer&rsquo;s request for their data, we compile the
              store-scoped data Dropskip holds for that customer and provide it
              to the merchant.
            </li>
            <li>
              <strong>customers/redact</strong> &mdash; we erase or de-identify
              that customer&rsquo;s records from Dropskip&rsquo;s stored copies.
            </li>
            <li>
              <strong>shop/redact</strong> &mdash; sent 48 hours after
              uninstall; we confirm the shop&rsquo;s configuration and synced
              data have been deleted.
            </li>
          </ul>
        </section>

        <section id="cookies" className={styles.section}>
          <h2>15. Cookies &amp; tracking</h2>
          <p>
            The embedded Shopify app uses only strictly necessary cookies and
            session storage required for authentication and to run inside the
            Shopify admin. The Dropskip platform uses cookies and local storage
            needed to keep you signed in and to remember basic preferences (such
            as your theme). We do not use third-party advertising cookies or
            cross-site tracking.
          </p>
        </section>

        <section id="children" className={styles.section}>
          <h2>16. Children&rsquo;s data</h2>
          <p>
            Dropskip is a business tool and is not directed to children. We do
            not knowingly collect personal data directly from children. Any
            customer personal data contained in a merchant&rsquo;s orders is
            processed on the merchant&rsquo;s behalf under their own privacy
            practices.
          </p>
        </section>

        <section id="changes" className={styles.section}>
          <h2>17. Changes to this policy</h2>
          <p>
            We may update this policy from time to time. When we make material
            changes we will update the &ldquo;Last updated&rdquo; date above
            and, where appropriate, notify you in the app or by email. Continued
            use of Dropskip after an update means you accept the revised policy.
          </p>
        </section>

        <section id="contact" className={styles.section}>
          <h2>18. Contact us</h2>
          <div className={styles.contact}>
            <p>
              <strong>{OPERATOR}</strong> &mdash; operator of Dropskip&trade;
            </p>
            <p>
              Email: <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>
            </p>
            <p>
              For privacy requests, please include your Shopify shop domain and
              a description of your request.
            </p>
          </div>
        </section>
      </main>

      <footer className={styles.footer}>
        Dropskip&trade; &mdash; forward-looking demand forecasting for Shopify
        merchants. Powered by {OPERATOR}.
      </footer>
    </div>
  );
}
